import React from 'react';
import { Box, Grid, Paper, Skeleton, useTheme } from '@mui/material';
import { useDelayedVisible } from '../LoadingSkeleton';

export interface DashboardSkeletonProps {
  /** `monthly` | `overview` | `branches` | `carbon` */
  variant?: 'monthly' | 'overview' | 'branches' | 'carbon';
  delayMs?: number;
}

const SectionBlock: React.FC<{
  titleWidth?: number;
  children: React.ReactNode;
  mb?: number;
}> = ({ titleWidth = 180, children, mb = 3 }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 3 },
        mb,
        borderRadius: '16px',
        bgcolor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[1],
      }}
    >
      <Skeleton variant="text" width={titleWidth} height={28} sx={{ mb: 2.5 }} />
      {children}
    </Paper>
  );
};

const StatCardsRow: React.FC<{ count?: number }> = ({ count = 6 }) => (
  <Grid container spacing={3}>
    {Array.from({ length: count }).map((_, i) => (
      <Grid item xs={12} sm={6} md={4} key={`stat-${i}`}>
        <Skeleton variant="rounded" height={132} sx={{ borderRadius: '16px' }} />
      </Grid>
    ))}
  </Grid>
);

const ChartRow: React.FC<{ count?: number }> = ({ count = 2 }) => (
  <Grid container spacing={3}>
    {Array.from({ length: count }).map((_, i) => (
      <Grid item xs={12} md={6} key={`chart-${i}`}>
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: '16px' }} />
      </Grid>
    ))}
  </Grid>
);

const BranchCards: React.FC = () => (
  <Grid container spacing={3}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Grid item xs={12} sm={6} lg={4} key={`branch-${i}`}>
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: '16px' }} />
      </Grid>
    ))}
  </Grid>
);

const CarbonPanel: React.FC = () => (
  <Box>
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
      <Skeleton variant="rounded" width={200} height={40} sx={{ borderRadius: '12px' }} />
      <Skeleton variant="rounded" width={200} height={40} sx={{ borderRadius: '12px' }} />
    </Box>
    <Grid container spacing={3}>
      {Array.from({ length: 4 }).map((_, i) => (
        <Grid item xs={12} sm={6} md={3} key={`carbon-stat-${i}`}>
          <Skeleton variant="rounded" height={120} sx={{ borderRadius: '16px' }} />
        </Grid>
      ))}
      <Grid item xs={12}>
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: '16px' }} />
      </Grid>
    </Grid>
  </Box>
);

const DashboardSkeleton: React.FC<DashboardSkeletonProps> = ({
  variant = 'monthly',
  delayMs = 250,
}) => {
  const visible = useDelayedVisible(delayMs);
  if (!visible) return null;

  if (variant === 'branches') {
    return (
      <SectionBlock titleWidth={160} mb={0}>
        <BranchCards />
      </SectionBlock>
    );
  }

  if (variant === 'carbon') {
    return (
      <SectionBlock titleWidth={200} mb={0}>
        <CarbonPanel />
      </SectionBlock>
    );
  }

  if (variant === 'overview') {
    return (
      <Box>
        <SectionBlock titleWidth={200}>
          <StatCardsRow count={3} />
        </SectionBlock>
        <SectionBlock titleWidth={180}>
          <StatCardsRow count={4} />
        </SectionBlock>
        <SectionBlock titleWidth={120} mb={0}>
          <ChartRow />
        </SectionBlock>
      </Box>
    );
  }

  return (
    <Box>
      <SectionBlock titleWidth={160}>
        <StatCardsRow count={3} />
      </SectionBlock>
      <SectionBlock titleWidth={200}>
        <StatCardsRow count={6} />
      </SectionBlock>
      <SectionBlock titleWidth={120} mb={0}>
        <ChartRow />
      </SectionBlock>
    </Box>
  );
};

export default DashboardSkeleton;
